import { normalizeSchedulerOfficeId } from "accountOwner/pages/Scheduler/scheduler.function";
import { isUnassignedSchedulerEvent } from "./unassignedEvents.utils";

/**
 * @param {string|null|undefined} name
 * @returns {string}
 */
const normalizeOfficeName = (name) =>
  (name || "").trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Unique trimmed office names from listing `officeNames` (normalized for dedupe).
 * @param {{ officeNames?: Array<string|null>|string|null }|null|undefined} user
 * @returns {string[]}
 */
export const getListingUserOfficeNames = (user) => {
  const raw = user?.officeNames;
  const names = Array.isArray(raw)
    ? raw
    : typeof raw === "string" && raw.trim()
      ? [raw]
      : [];

  const uniqueByNormalized = new Map();
  names.forEach((name) => {
    if (name == null) return;
    const trimmed = String(name).trim();
    if (!trimmed) return;
    const key = normalizeOfficeName(trimmed);
    if (!key || uniqueByNormalized.has(key)) return;
    uniqueByNormalized.set(key, trimmed);
  });

  return [...uniqueByNormalized.values()];
};

/**
 * @param {{ officeNames?: Array<string|null>|string|null }|null|undefined} user
 * @returns {Array<{ name: string }>}
 */
export const getListingUserOffices = (user) =>
  getListingUserOfficeNames(user).map((name) => ({ name }));

/**
 * Whether a listing user belongs to the shift office (by `officeNames`).
 * @param {{ officeNames?: Array<string|null>|string|null }|null|undefined} targetUser
 * @param {string|null|undefined} shiftOfficeName
 * @returns {boolean}
 */
export const targetUserBelongsToShiftOffice = (targetUser, shiftOfficeName) => {
  const normalizedShiftOffice = normalizeOfficeName(shiftOfficeName);
  if (!normalizedShiftOffice) return false;

  return getListingUserOfficeNames(targetUser).some(
    (name) => normalizeOfficeName(name) === normalizedShiftOffice,
  );
};

/**
 * @param {number|string|null|undefined} targetUserId
 * @param {number|string|null|undefined} accountOwnerUserId
 * @returns {boolean}
 */
export const isDropTargetAccountOwner = (targetUserId, accountOwnerUserId) =>
  targetUserId != null &&
  accountOwnerUserId != null &&
  String(targetUserId) === String(accountOwnerUserId);

/**
 * Count of active unique staff assigned to an event/shift.
 * Prefers `eventEmployees` when present; falls back to listing `eventEmployeeCount`.
 * @param {object|null|undefined} eventData
 * @returns {number}
 */
export const getActiveAssigneeCount = (eventData) => {
  const activeEmployees = (eventData?.eventEmployees || []).filter(
    (entry) => !entry?.isDeletedForThisSchedulerEvent,
  );

  if (activeEmployees.length) {
    const uniqueUserIds = new Set(
      activeEmployees
        .map((entry) => entry?.userId ?? entry?.user?.id)
        .filter((id) => id != null && id !== "")
        .map(String),
    );
    return uniqueUserIds.size;
  }

  const listingCount =
    eventData?.eventEmployeeCount ?? eventData?.modifiedEmployeeCount;
  return Number.isFinite(Number(listingCount)) ? Number(listingCount) : 0;
};

/**
 * Target staff unique office count from listing `officeNames`.
 * @param {{ officeNames?: Array<string|null>|string|null }|null|undefined} targetUser
 * @returns {number}
 */
export const getTargetUserOfficeNameCount = (targetUser) =>
  getListingUserOfficeNames(targetUser).length;

/**
 * Resolves the sole office id when the target listing user has exactly one office.
 * @returns {number|null}
 */
export const resolveTargetUserSingleOfficeId = (
  targetUser,
  ownerOfficeList = [],
) => {
  const offices = getListingUserOffices(targetUser);
  if (offices.length !== 1) return null;
  return resolveOfficeIdFromName(ownerOfficeList, offices[0]?.name);
};

/**
 * Shift staff-to-staff early validation using listing API fields only.
 * Multi-assignee shifts mirror Select Staff on create/edit: only same-office
 * staff are allowed. Single-assignee shifts may be reassigned to another user
 * (office resolved later by {@link validateStaffCellDropAssignment}).
 * Returns null when this validator does not apply.
 * @param {object} params
 * @param {object} [params.eventData] Listing / event item used for assignee count
 */
export const validateShiftListingStaffDrop = ({
  isShift,
  shiftOfficeName,
  sourceUserId,
  targetUserId,
  targetUser,
  isUnassignedSource,
  ownerOfficeList = [],
  accountOwnerUserId,
  eventData,
  t,
}) => {
  if (!isShift || isUnassignedSource) return null;
  if (!targetUserId || !sourceUserId) return null;
  if (String(sourceUserId) === String(targetUserId)) return null;

  if (isDropTargetAccountOwner(targetUserId, accountOwnerUserId)) {
    const resolvedShiftOfficeId = resolveOfficeIdFromName(
      ownerOfficeList,
      shiftOfficeName?.trim() || null,
    );
    return {
      action: "proceed",
      shiftSourceOfficeId: resolvedShiftOfficeId,
    };
  }

  // Single-assignee shift: allow drop; full validator reassigns + resolves office.
  if (getActiveAssigneeCount(eventData) <= 1) {
    return { action: "proceed" };
  }

  const resolvedShiftOfficeName = shiftOfficeName?.trim() || null;
  if (!resolvedShiftOfficeName) {
    return {
      action: "block",
      message: t("scheduler.shiftDragStaffDifferentOffice"),
    };
  }

  if (!targetUserBelongsToShiftOffice(targetUser, resolvedShiftOfficeName)) {
    return {
      action: "block",
      message: t("scheduler.shiftDragStaffDifferentOffice"),
    };
  }

  return {
    action: "proceed",
    shiftSourceOfficeId: resolveOfficeIdFromName(
      ownerOfficeList,
      resolvedShiftOfficeName,
    ),
  };
};

/**
 * @param {Array<{ id?: number, name?: string }>} offices
 * @param {string|null|undefined} officeName
 * @returns {boolean}
 */
export const userBelongsToOfficeByName = (offices = [], officeName) => {
  const normalized = normalizeOfficeName(officeName);
  if (!normalized) return false;
  return offices.some(
    (office) => normalizeOfficeName(office?.name) === normalized,
  );
};

/**
 * @param {Array<{ id: number, name?: string }>} offices
 * @param {string|null|undefined} officeName
 * @returns {number|null}
 */
export const resolveOfficeIdFromName = (offices = [], officeName) => {
  const normalized = normalizeOfficeName(officeName);
  if (!normalized) return null;
  const match = offices.find(
    (office) => normalizeOfficeName(office?.name) === normalized,
  );
  return normalizeSchedulerOfficeId(match?.id);
};

/**
 * @param {object} eventData
 * @param {number|string|null|undefined} userId
 * @param {{ officeId?: number|string, officeName?: string }} [sourceContext]
 * @returns {number|null}
 */
export const getSchedulerEmployeeOfficeId = (eventData, userId, sourceContext) => {
  const contextOfficeId = normalizeSchedulerOfficeId(sourceContext?.officeId);
  const contextOfficeName = sourceContext?.officeName;

  if (userId == null) {
    return (
      contextOfficeId ?? normalizeSchedulerOfficeId(eventData?.officeId)
    );
  }

  const activeEmployees = (eventData?.eventEmployees || []).filter(
    (entry) => !entry?.isDeletedForThisSchedulerEvent,
  );

  const employee = activeEmployees.find((entry) => {
    if (String(entry?.userId ?? entry?.user?.id) !== String(userId)) {
      return false;
    }
    if (contextOfficeId) {
      return (
        normalizeSchedulerOfficeId(entry?.officeId) === contextOfficeId
      );
    }
    if (contextOfficeName) {
      const employeeOfficeName =
        entry?.officeName ?? entry?.office?.name ?? entry?.user?.officeName;
      return (
        normalizeOfficeName(employeeOfficeName) ===
        normalizeOfficeName(contextOfficeName)
      );
    }
    return true;
  });

  return (
    normalizeSchedulerOfficeId(employee?.officeId) ??
    contextOfficeId ??
    normalizeSchedulerOfficeId(eventData?.officeId)
  );
};

/**
 * Stable key for matching a specific user+office assignment row.
 * @param {{ userId?: number|string, user?: { id?: number|string }, officeId?: number|string }} employee
 * @returns {string}
 */
export const schedulerEmployeeOfficeKey = (employee) =>
  `${employee?.userId ?? employee?.user?.id ?? ""}:${
    normalizeSchedulerOfficeId(employee?.officeId) ?? ""
  }`;

/**
 * Resolves the `eventEmployees` row for the staff cell the user dragged from.
 * @param {Array<object>} eventEmployees
 * @param {{ sourceUserId?: number|string, sourceOfficeId?: number|string, sourceOfficeName?: string, shiftSourceOfficeId?: number|string }} dropContext
 * @returns {object|null}
 */
export const resolveDraggedSourceEmployee = (eventEmployees = [], dropContext = {}) => {
  const activeEmployees = (eventEmployees || []).filter(
    (entry) => !entry?.isDeletedForThisSchedulerEvent,
  );
  const sourceUserId = dropContext?.sourceUserId;
  if (sourceUserId == null || sourceUserId === "") return null;

  const sourceOfficeId = normalizeSchedulerOfficeId(
    dropContext?.sourceOfficeId ?? dropContext?.shiftSourceOfficeId,
  );
  const sourceOfficeName = dropContext?.sourceOfficeName ?? null;

  const matchedEmployee = activeEmployees.find((entry) => {
    if (String(entry?.userId ?? entry?.user?.id) !== String(sourceUserId)) {
      return false;
    }
    if (sourceOfficeId) {
      return normalizeSchedulerOfficeId(entry?.officeId) === sourceOfficeId;
    }
    if (sourceOfficeName) {
      const employeeOfficeName =
        entry?.officeName ?? entry?.office?.name ?? entry?.user?.officeName;
      return (
        normalizeOfficeName(employeeOfficeName) ===
        normalizeOfficeName(sourceOfficeName)
      );
    }
    return true;
  });

  if (matchedEmployee) return matchedEmployee;

  return (
    activeEmployees.find(
      (entry) =>
        String(entry?.userId ?? entry?.user?.id) === String(sourceUserId),
    ) ?? null
  );
};

/**
 * Resolves the shift office from drag context and event details (no API calls).
 */
export const resolveShiftSourceOffice = ({
  eventData,
  sourceUserId,
  sourceContext,
  dropContext,
  ownerOfficeList = [],
}) => {
  const mergedSourceContext = {
    officeId:
      sourceContext?.officeId ??
      dropContext?.sourceOfficeId ??
      eventData?.officeId,
    officeName:
      sourceContext?.officeName ??
      dropContext?.sourceOfficeName ??
      eventData?.officeName ??
      null,
  };

  const officeName = mergedSourceContext.officeName || null;

  let officeId =
    getSchedulerEmployeeOfficeId(eventData, sourceUserId, mergedSourceContext) ??
    normalizeSchedulerOfficeId(mergedSourceContext.officeId);

  if (!officeId && officeName && ownerOfficeList.length) {
    officeId = resolveOfficeIdFromName(ownerOfficeList, officeName);
  }

  return { officeId, officeName };
};

/**
 * Office id for a shift staff reassignment.
 * Prefers an explicit target office (cross-office single-assignee / office modal),
 * otherwise preserves the shift's existing office.
 */
export const resolveShiftStaffChangeOfficeId = (
  eventData,
  dropContext,
  sourceUserId,
  originalActiveEmployees = [],
  ownerOfficeList = [],
) => {
  const sourceContext = {
    officeId: dropContext?.sourceOfficeId,
    officeName: dropContext?.sourceOfficeName,
  };
  const sourceEmployee = originalActiveEmployees.find(
    (employee) =>
      String(employee?.userId ?? employee?.user?.id) === String(sourceUserId),
  );

  return (
    normalizeSchedulerOfficeId(dropContext?.targetOfficeId) ??
    normalizeSchedulerOfficeId(dropContext?.shiftSourceOfficeId) ??
    getSchedulerEmployeeOfficeId(eventData, sourceUserId, sourceContext) ??
    normalizeSchedulerOfficeId(sourceEmployee?.officeId) ??
    resolveOfficeIdFromName(ownerOfficeList, dropContext?.sourceOfficeName) ??
    normalizeSchedulerOfficeId(eventData?.officeId)
  );
};

/**
 * @param {Array<{ id: number }>} offices
 * @param {number|string|null|undefined} officeId
 * @returns {boolean}
 */
export const userBelongsToOffice = (offices = [], officeId) => {
  const normalized = normalizeSchedulerOfficeId(officeId);
  if (!normalized) return false;
  return offices.some(
    (office) => normalizeSchedulerOfficeId(office?.id) === normalized,
  );
};

/**
 * Resolves office + enterprise flags when assigning an event to staff.
 */
export const resolveEventAssignmentOffice = ({
  eventData,
  sourceUserId,
  targetUserOffices = [],
  cellOfficeId,
  explicitTargetOfficeId,
}) => {
  const resolvedExplicit = normalizeSchedulerOfficeId(explicitTargetOfficeId);
  if (resolvedExplicit) {
    const eventOfficeId = getSchedulerEmployeeOfficeId(eventData, sourceUserId);
    const makeEnterprise = Boolean(
      eventOfficeId && eventOfficeId !== resolvedExplicit,
    );
    return { officeId: resolvedExplicit, makeEnterprise };
  }

  const eventOfficeId = getSchedulerEmployeeOfficeId(eventData, sourceUserId);
  const normalizedCellOfficeId = normalizeSchedulerOfficeId(cellOfficeId);

  if (
    eventOfficeId &&
    userBelongsToOffice(targetUserOffices, eventOfficeId)
  ) {
    return { officeId: eventOfficeId, makeEnterprise: false };
  }

  const fallbackOfficeId =
    (normalizedCellOfficeId &&
    userBelongsToOffice(targetUserOffices, normalizedCellOfficeId)
      ? normalizedCellOfficeId
      : null) ??
    normalizeSchedulerOfficeId(targetUserOffices[0]?.id);

  const makeEnterprise = Boolean(
    eventOfficeId &&
    fallbackOfficeId &&
    eventOfficeId !== fallbackOfficeId,
  );

  return { officeId: fallbackOfficeId, makeEnterprise };
};

/**
 * First non-empty trimmed office name from candidate values.
 * @param {...(string|null|undefined)} candidates
 * @returns {string|null}
 */
const firstOfficeName = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const trimmed = String(candidate).trim();
    if (trimmed) return trimmed;
  }
  return null;
};

/**
 * Resolves office id + name for an event/shift from drag context and event payload.
 * Draft/unassigned listing rows may expose the office via alternate fields
 * (`eventEmployeeOfficeName`, `officeNameList`, nested `office`, published offices)
 * instead of root `officeId` / `officeName`.
 *
 * @returns {{ officeId: number|null, officeName: string|null }}
 */
export const resolveEventOrShiftOffice = ({
  eventData,
  sourceContext,
  dropContext,
  ownerOfficeList = [],
} = {}) => {
  const publishedOffices = Array.isArray(
    eventData?.schedulerEventPublishedOffices,
  )
    ? eventData.schedulerEventPublishedOffices
    : [];
  const singlePublishedOffice =
    publishedOffices.length === 1 ? publishedOffices[0] : null;

  let officeId =
    normalizeSchedulerOfficeId(sourceContext?.officeId) ??
    normalizeSchedulerOfficeId(dropContext?.sourceOfficeId) ??
    normalizeSchedulerOfficeId(dropContext?.shiftSourceOfficeId) ??
    normalizeSchedulerOfficeId(eventData?.officeId) ??
    normalizeSchedulerOfficeId(eventData?.office?.id) ??
    normalizeSchedulerOfficeId(eventData?.office?.officeId) ??
    normalizeSchedulerOfficeId(singlePublishedOffice?.officeId) ??
    normalizeSchedulerOfficeId(singlePublishedOffice?.id);

  let officeName = firstOfficeName(
    sourceContext?.officeName,
    dropContext?.sourceOfficeName,
    eventData?.officeName,
    eventData?.eventEmployeeOfficeName,
    eventData?.officeNameList,
    eventData?.office?.name,
    eventData?.office?.officeName,
    singlePublishedOffice?.officeName,
    singlePublishedOffice?.name,
  );

  if (!officeId && officeName && ownerOfficeList.length) {
    officeId = resolveOfficeIdFromName(ownerOfficeList, officeName);
  }

  if (!officeName && officeId && ownerOfficeList.length) {
    officeName =
      ownerOfficeList.find(
        (office) => normalizeSchedulerOfficeId(office?.id) === officeId,
      )?.name ?? null;
  }

  return { officeId, officeName };
};

/**
 * If the target listing user belongs to the given event/shift office, return that office id.
 * Listing users only expose `officeNames`, so id matching goes through `ownerOfficeList` names.
 * @returns {number|null}
 */
export const resolveSharedOfficeIdForTargetUser = ({
  targetUser,
  officeId,
  officeName,
  ownerOfficeList = [],
}) => {
  const targetUserOffices = getListingUserOffices(targetUser);
  if (!targetUserOffices.length) return null;

  const normalizedOfficeId = normalizeSchedulerOfficeId(officeId);
  const resolvedOfficeName =
    firstOfficeName(officeName) ||
    (normalizedOfficeId
      ? ownerOfficeList.find(
          (office) =>
            normalizeSchedulerOfficeId(office?.id) === normalizedOfficeId,
        )?.name
      : null);

  if (
    resolvedOfficeName &&
    userBelongsToOfficeByName(targetUserOffices, resolvedOfficeName)
  ) {
    return (
      normalizedOfficeId ??
      resolveOfficeIdFromName(ownerOfficeList, resolvedOfficeName)
    );
  }

  // Fall back to id match when the shift office name is missing/mismatched but
  // the target's listing office names resolve to the same office id.
  if (normalizedOfficeId && ownerOfficeList.length) {
    const matchedById = targetUserOffices.some(
      (office) =>
        resolveOfficeIdFromName(ownerOfficeList, office?.name) ===
        normalizedOfficeId,
    );
    if (matchedById) return normalizedOfficeId;
  }

  return null;
};

/**
 * Resolves office id when assigning an unassigned draft to staff.
 */
export const resolveUnassignedToStaffOfficeId = ({
  dropContext,
  sourceContext,
  eventData,
  ownerOfficeList = [],
}) => {
  const targetOfficeNames = getListingUserOfficeNames(dropContext?.targetUser);
  const singleTargetOfficeId =
    targetOfficeNames.length === 1
      ? resolveOfficeIdFromName(ownerOfficeList, targetOfficeNames[0])
      : null;

  const { officeId: resolvedSourceOfficeId, officeName: resolvedSourceOfficeName } =
    resolveEventOrShiftOffice({
      eventData,
      sourceContext,
      dropContext,
      ownerOfficeList,
    });

  return (
    normalizeSchedulerOfficeId(dropContext?.targetOfficeId) ??
    resolvedSourceOfficeId ??
    singleTargetOfficeId ??
    resolveOfficeIdFromName(ownerOfficeList, resolvedSourceOfficeName) ??
    (ownerOfficeList.length === 1
      ? normalizeSchedulerOfficeId(ownerOfficeList[0]?.id)
      : null)
  );
};

/**
 * Validates staff-cell drop before persisting assignment changes.
 * Uses listing user `officeNames` only — no `Office/byOwnerAndUser` call.
 *
 * @returns {{
 *   action: 'proceed' | 'block' | 'showOfficeModal',
 *   message?: string,
 *   targetOfficeId?: number|null,
 *   shiftSourceOfficeId?: number|null,
 *   makeEnterprise?: boolean,
 *   isUnassignedSource?: boolean,
 *   isShift?: boolean,
 * }}
 */
export const validateStaffCellDropAssignment = ({
  eventData,
  dropContext,
  sourceContext,
  ownerOfficeList = [],
  accountOwnerUserId,
  t,
}) => {
  const targetUserId = dropContext?.targetUserId;
  const explicitSourceUserId =
    sourceContext?.userId ?? dropContext?.sourceUserId ?? null;
  const sourceUserId = explicitSourceUserId ?? eventData?.userId ?? null;
  // Dragging from a staff row is never an "unassigned" source, even if listing
  // metadata omits employee counts (details still carry eventEmployees).
  const isUnassignedSource =
    Boolean(sourceContext?.isUnassigned) ||
    Boolean(dropContext?.isUnassignedSource) ||
    (explicitSourceUserId == null && isUnassignedSchedulerEvent(eventData));
  const isShift = Boolean(
    dropContext?.isShift ?? sourceContext?.isShift ?? eventData?.isShift,
  );
  const isSameUserMove =
    sourceUserId &&
    targetUserId &&
    String(sourceUserId) === String(targetUserId);

  const hasStaffChange = Boolean(
    targetUserId &&
    (isUnassignedSource ||
      (sourceUserId && String(sourceUserId) !== String(targetUserId))),
  );

  if (!hasStaffChange || isSameUserMove) {
    return {
      action: "proceed",
      targetOfficeId: dropContext?.targetOfficeId ?? null,
      isUnassignedSource,
    };
  }

  if (isDropTargetAccountOwner(targetUserId, accountOwnerUserId)) {
    // Unassigned → account owner still needs a concrete office on the employee row.
    if (isUnassignedSource) {
      const targetOfficeId = resolveUnassignedToStaffOfficeId({
        dropContext,
        sourceContext,
        eventData,
        ownerOfficeList,
      });

      if (!targetOfficeId) {
        return {
          action: "showOfficeModal",
          isUnassignedSource,
          isShift,
        };
      }

      return {
        action: "proceed",
        targetOfficeId,
        shiftSourceOfficeId: isShift ? targetOfficeId : undefined,
        makeEnterprise: false,
        isUnassignedSource,
        isShift,
      };
    }

    if (isShift) {
      const { officeId: sourceOfficeId, officeName: sourceOfficeName } =
        resolveShiftSourceOffice({
          eventData,
          sourceUserId,
          sourceContext,
          dropContext,
          ownerOfficeList,
        });
      const shiftOfficeName =
        dropContext?.sourceOfficeName ??
        sourceContext?.officeName ??
        sourceOfficeName ??
        (sourceOfficeId
          ? ownerOfficeList.find(
              (office) =>
                normalizeSchedulerOfficeId(office?.id) === sourceOfficeId,
            )?.name
          : null);
      const preservedShiftOfficeId =
        sourceOfficeId ?? resolveOfficeIdFromName(ownerOfficeList, shiftOfficeName);

      return {
        action: "proceed",
        targetOfficeId: preservedShiftOfficeId,
        shiftSourceOfficeId: preservedShiftOfficeId,
        isUnassignedSource,
        isShift: true,
      };
    }

    const eventOfficeId = getSchedulerEmployeeOfficeId(
      eventData,
      isUnassignedSource ? null : sourceUserId,
      sourceContext,
    );
    const eventOfficeName =
      sourceContext?.officeName ??
      dropContext?.sourceOfficeName ??
      eventData?.officeName ??
      (eventOfficeId
        ? ownerOfficeList.find(
            (office) =>
              normalizeSchedulerOfficeId(office?.id) === eventOfficeId,
          )?.name
        : null);

    return {
      action: "proceed",
      targetOfficeId:
        eventOfficeId ??
        resolveOfficeIdFromName(ownerOfficeList, eventOfficeName),
      makeEnterprise: false,
      isUnassignedSource,
      isShift: false,
    };
  }

  const targetOfficeNameCount = getTargetUserOfficeNameCount(
    dropContext?.targetUser,
  );

  /**
   * Unassigned / cross-office target with no shared office:
   * - 1 office on target → auto-assign (no popup)
   * - >1 offices on target → show office popup
   */
  const resolveAmbiguousTargetOffice = ({
    isShiftAssignment,
    sourceOfficeIdForEnterprise = null,
    allowUnassignedOfficeFallback = false,
  }) => {
    if (targetOfficeNameCount > 1) {
      return {
        action: "showOfficeModal",
        isUnassignedSource,
        isShift: isShiftAssignment,
      };
    }

    let targetOfficeId = resolveTargetUserSingleOfficeId(
      dropContext?.targetUser,
      ownerOfficeList,
    );

    // Only unassigned drops may fall back to event/source office ids.
    // Assigned cross-office drops must use the target staff's office.
    if (!targetOfficeId && allowUnassignedOfficeFallback) {
      targetOfficeId = resolveUnassignedToStaffOfficeId({
        dropContext,
        sourceContext,
        eventData,
        ownerOfficeList,
      });
    }

    if (!targetOfficeId) {
      return {
        action: "showOfficeModal",
        isUnassignedSource,
        isShift: isShiftAssignment,
      };
    }

    if (isShiftAssignment) {
      return {
        action: "proceed",
        targetOfficeId,
        shiftSourceOfficeId: targetOfficeId,
        isUnassignedSource,
        isShift: true,
      };
    }

    const makeEnterprise = Boolean(
      sourceOfficeIdForEnterprise &&
        sourceOfficeIdForEnterprise !== targetOfficeId,
    );

    return {
      action: "proceed",
      targetOfficeId,
      makeEnterprise,
      isUnassignedSource,
      isShift: false,
    };
  };

  if (isShift) {
    if (isUnassignedSource) {
      const { officeId: shiftOfficeId, officeName: shiftOfficeName } =
        resolveEventOrShiftOffice({
          eventData,
          sourceContext,
          dropContext,
          ownerOfficeList,
        });

      const sharedOfficeId = resolveSharedOfficeIdForTargetUser({
        targetUser: dropContext?.targetUser,
        officeId: shiftOfficeId,
        officeName: shiftOfficeName,
        ownerOfficeList,
      });
      if (sharedOfficeId) {
        return {
          action: "proceed",
          targetOfficeId: sharedOfficeId,
          shiftSourceOfficeId: sharedOfficeId,
          isUnassignedSource,
          isShift: true,
        };
      }

      // Name match can succeed even when ownerOfficeList cannot map to an id above.
      if (
        shiftOfficeName &&
        targetUserBelongsToShiftOffice(
          dropContext?.targetUser,
          shiftOfficeName,
        )
      ) {
        const preservedOfficeId =
          shiftOfficeId ??
          resolveOfficeIdFromName(ownerOfficeList, shiftOfficeName);
        if (preservedOfficeId) {
          return {
            action: "proceed",
            targetOfficeId: preservedOfficeId,
            shiftSourceOfficeId: preservedOfficeId,
            isUnassignedSource,
            isShift: true,
          };
        }
      }

      // Draft/unassigned shift already has an office: never prompt for office again.
      // Either assign with that office (above) or block when staff is not in it.
      if (shiftOfficeId || shiftOfficeName) {
        return {
          action: "block",
          message: t("scheduler.shiftDragStaffDifferentOffice"),
          isShift: true,
        };
      }

      // Unassigned shift with no office yet: popup only when target has multiple offices.
      return resolveAmbiguousTargetOffice({
        isShiftAssignment: true,
        allowUnassignedOfficeFallback: true,
      });
    }

    const { officeId: sourceOfficeId, officeName: sourceOfficeName } =
      resolveShiftSourceOffice({
        eventData,
        sourceUserId,
        sourceContext,
        dropContext,
        ownerOfficeList,
      });

    const shiftOfficeName =
      dropContext?.sourceOfficeName ??
      sourceContext?.officeName ??
      sourceOfficeName ??
      (sourceOfficeId
        ? ownerOfficeList.find(
            (office) =>
              normalizeSchedulerOfficeId(office?.id) === sourceOfficeId,
          )?.name
        : null);

    const sharedShiftOfficeId = resolveSharedOfficeIdForTargetUser({
      targetUser: dropContext?.targetUser,
      officeId: sourceOfficeId,
      officeName: shiftOfficeName,
      ownerOfficeList,
    });
    if (sharedShiftOfficeId) {
      return {
        action: "proceed",
        targetOfficeId: sharedShiftOfficeId,
        shiftSourceOfficeId: sharedShiftOfficeId,
        isUnassignedSource,
        isShift: true,
      };
    }

    if (targetUserBelongsToShiftOffice(dropContext?.targetUser, shiftOfficeName)) {
      const preservedShiftOfficeId =
        sourceOfficeId ??
        resolveOfficeIdFromName(ownerOfficeList, shiftOfficeName);

      return {
        action: "proceed",
        targetOfficeId: preservedShiftOfficeId,
        shiftSourceOfficeId: preservedShiftOfficeId,
        isUnassignedSource,
        isShift: true,
      };
    }

    // Multi-assignee shift → different office: restrict (Select Staff same-office lock).
    // Single-assignee: reassign to target staff and resolve their office (popup if >1).
    if (getActiveAssigneeCount(eventData) > 1) {
      return {
        action: "block",
        message: t("scheduler.shiftDragStaffDifferentOffice"),
        isShift: true,
      };
    }

    return resolveAmbiguousTargetOffice({
      isShiftAssignment: true,
    });
  }

  const eventSourceUserId = isUnassignedSource ? null : sourceUserId;
  const eventOfficeId = getSchedulerEmployeeOfficeId(
    eventData,
    eventSourceUserId,
    sourceContext,
  );
  const eventOfficeName =
    sourceContext?.officeName ??
    dropContext?.sourceOfficeName ??
    eventData?.officeName ??
    (eventOfficeId
      ? ownerOfficeList.find(
          (office) =>
            normalizeSchedulerOfficeId(office?.id) === eventOfficeId,
        )?.name
      : null);

  if (isUnassignedSource) {
    const sharedOfficeId = resolveSharedOfficeIdForTargetUser({
      targetUser: dropContext?.targetUser,
      officeId: eventOfficeId,
      officeName: eventOfficeName,
      ownerOfficeList,
    });
    if (sharedOfficeId) {
      return {
        action: "proceed",
        targetOfficeId: sharedOfficeId,
        makeEnterprise: false,
        isUnassignedSource,
        isShift: false,
      };
    }

    // Unassigned event → staff: popup only when target has multiple offices.
    return resolveAmbiguousTargetOffice({
      isShiftAssignment: false,
      sourceOfficeIdForEnterprise: eventOfficeId,
      allowUnassignedOfficeFallback: true,
    });
  }

  const sharedOfficeId = resolveSharedOfficeIdForTargetUser({
    targetUser: dropContext?.targetUser,
    officeId: eventOfficeId,
    officeName: eventOfficeName,
    ownerOfficeList,
  });
  if (sharedOfficeId) {
    return {
      action: "proceed",
      targetOfficeId: sharedOfficeId,
      makeEnterprise: false,
      isUnassignedSource,
      isShift: false,
    };
  }

  // Event → different office (1 or many assignees):
  // popup if target has multiple offices; else auto + enterprise when offices differ.
  // Multi-assignee: prior offices remain on other employees; target office is added.
  return resolveAmbiguousTargetOffice({
    isShiftAssignment: false,
    sourceOfficeIdForEnterprise: eventOfficeId,
  });
};
